import { Synth } from './synth';

/** Every sound the game can make. */
export type Cue =
  | 'build'
  | 'remove'
  | 'deny'
  | 'limit'
  | 'energize'
  | 'houseLit'
  | 'win'
  | 'perfect'
  | 'engineers'
  | 'newBest'
  | 'click'
  | 'clickOff'
  | 'beat'
  | 'copy'
  | 'copyFail';

/**
 * The one scale (STYLEGUIDE §9): F major pentatonic voiced from C5, eight
 * rungs. Every melodic onset in the game sits on a rung, which is why stacked
 * cues never sound sour together. Whisper-gain partials riding another note's
 * onset are timbre, not melody, and may leave the collection.
 */
export const SCALE = [523.25, 587.33, 698.46, 783.99, 880.0, 1046.5, 1174.66, 1396.91];

const [C5, , F5, , A5, C6, D6, F6] = SCALE;

/**
 * The hum — two sawtooths beating around 120 Hz, the sound of a transformer
 * on 60 Hz mains. Deliberately not a note: a move the grid refuses gets
 * physics, never music.
 */
function hum(s: Synth, delay = 0): void {
  s.tone({ freq: 120, type: 'sawtooth', duration: 0.12, gain: 0.16, delay });
  s.tone({ freq: 123, type: 'sawtooth', duration: 0.12, gain: 0.16, delay });
}

/**
 * The sound design, one function per cue.
 *
 * The palette is deliberately electrical (STYLEGUIDE §9): filtered noise for
 * matter, clean tones for current, and the hum for refusal. Gains follow the
 * mix tiers — whisper 0.05–0.09, tick 0.10–0.14, move 0.15–0.22 — and the
 * payoffs get bigger by adding voices, never by getting louder.
 */
const CUES: Record<Cue, (s: Synth, step: number) => void> = {
  // steel settling onto ground — the glide passes through pitch, never onto a rung
  build: (s) => {
    s.noise({ duration: 0.11, gain: 0.22, cutoff: 2600, cutoffTo: 420 });
    s.tone({ freq: 210, to: 420, type: 'triangle', duration: 0.1, gain: 0.2 });
  },

  // the same, reversed and duller — something coming down
  remove: (s) => {
    s.noise({ duration: 0.13, gain: 0.2, cutoff: 900, cutoffTo: 220 });
    s.tone({ freq: 340, to: 150, type: 'triangle', duration: 0.12, gain: 0.18 });
  },

  // not a failure sting, just "not there"
  deny: (s) => hum(s),

  // the hum, twice — you are out of towers
  limit: (s) => {
    hum(s);
    hum(s, 0.13);
  },

  /**
   * Power reaching a tower. `step` climbs the scale as the surge travels, so a
   * long line plays as a rising run rather than the same blip repeated.
   */
  energize: (s, step) => {
    const note = SCALE[Math.min(step, SCALE.length - 1)];
    s.tone({ freq: note, type: 'triangle', duration: 0.13, gain: 0.16, attack: 0.004 });
    s.tone({ freq: note * 2, type: 'sine', duration: 0.09, gain: 0.07 });
  },

  // a house getting its lights: warm, bell-like, two rungs above the run
  houseLit: (s, step) => {
    const note = SCALE[Math.min(step + 2, SCALE.length - 1)];
    s.tone({ freq: note, type: 'sine', duration: 0.42, gain: 0.2, attack: 0.008 });
    s.tone({ freq: note * 1.5, type: 'sine', duration: 0.3, gain: 0.09, delay: 0.02 });
    s.noise({ duration: 0.18, gain: 0.05, cutoff: 5200, cutoffTo: 900 });
  },

  // the grid comes alive: the scale's own tonic triad, rising
  win: (s) => {
    [C5, F5, A5].forEach((freq, i) => {
      s.tone({ freq, type: 'triangle', duration: 0.5, gain: 0.17, delay: i * 0.075 });
      s.tone({ freq: freq * 2, type: 'sine', duration: 0.35, gain: 0.06, delay: i * 0.075 });
    });
  },

  // the perfect grid: the triad capped an octave above its floor, with shimmer
  perfect: (s) => {
    [C5, F5, A5, C6].forEach((freq, i) => {
      s.tone({ freq, type: 'triangle', duration: 0.75, gain: 0.16, delay: i * 0.085 });
      s.tone({ freq: freq * 2, type: 'sine', duration: 0.6, gain: 0.07, delay: i * 0.085 });
    });
    s.noise({ duration: 0.9, gain: 0.07, cutoff: 900, cutoffTo: 7000, attack: 0.25, delay: 0.15 });
    // G6 — a sparkle whose pitch class is still in the collection
    s.tone({ freq: 1567.98, type: 'sine', duration: 0.9, gain: 0.09, delay: 0.34 });
  },

  // the reference beaten: the perfect fanfare carried one rung higher, its
  // shimmer a shade brighter. The same moment, a register up — never louder.
  engineers: (s) => {
    [C5, F5, A5, C6, F6].forEach((freq, i) => {
      s.tone({ freq, type: 'triangle', duration: 0.75, gain: 0.15, delay: i * 0.085 });
      s.tone({ freq: freq * 2, type: 'sine', duration: 0.6, gain: 0.06, delay: i * 0.085 });
    });
    s.noise({ duration: 0.9, gain: 0.08, cutoff: 900, cutoffTo: 8200, attack: 0.25, delay: 0.15 });
    // the same G6 sparkle as `perfect`, saved for after the fifth note lands
    s.tone({ freq: 1567.98, type: 'sine', duration: 0.9, gain: 0.09, delay: 0.42 });
  },

  // two notes up: you found something shorter
  newBest: (s) => {
    s.tone({ freq: F5, type: 'triangle', duration: 0.14, gain: 0.16 });
    s.tone({ freq: C6, type: 'triangle', duration: 0.22, gain: 0.16, delay: 0.09 });
  },

  // a grain of noise with one D6 partial — chrome speaks in ticks
  click: (s) => {
    s.noise({ duration: 0.035, gain: 0.13, cutoff: 5200, cutoffTo: 2400 });
    s.tone({ freq: D6, type: 'sine', duration: 0.04, gain: 0.07 });
  },

  // the click's grain thrown the other way: duller, darker, no partial — a
  // breaker coming off is matter, not current
  clickOff: (s) => {
    s.noise({ duration: 0.04, gain: 0.12, cutoff: 1600, cutoffTo: 600 });
  },

  // the result card counting itself out, one chip at a time
  beat: (s, step) => {
    s.tone({
      freq: SCALE[Math.min(step, SCALE.length - 1)],
      type: 'sine',
      duration: 0.11,
      gain: 0.12,
    });
  },

  // the result leaves the building — the only chrome cue allowed two notes
  copy: (s) => {
    s.tone({ freq: A5, type: 'sine', duration: 0.08, gain: 0.13 });
    s.tone({ freq: D6, type: 'sine', duration: 0.12, gain: 0.11, delay: 0.06 });
  },

  // a blocked clipboard is a period, not a fault — a dull tick, never the hum
  copyFail: (s) => {
    s.noise({ duration: 0.06, gain: 0.12, cutoff: 1400, cutoffTo: 500 });
  },
};

/** How long `newBest` rings, in seconds — shared with the HUD flash (one clock). */
export const NEW_BEST_RING_S = 0.32;

/**
 * How long each move-tier cue rings, in seconds. Cues absent here are ticks,
 * whispers, or payoffs and never count against the cap.
 */
const MOVE_RING: Partial<Record<Cue, number>> = {
  build: 0.12,
  remove: 0.13,
  deny: 0.13,
  limit: 0.26,
  energize: 0.14,
  houseLit: 0.42,
  newBest: NEW_BEST_RING_S,
};

/** No more than three move-tier voices at once (STYLEGUIDE §9). */
const MOVE_CAP = 3;

/** Past the cap a cue still plays, but at whisper. */
const DUCK = 0.4;

/**
 * How long a cue parked behind a waking context stays worth playing, in ms.
 * Generous for a same-gesture resume (well under 50ms on any desktop), stingy
 * for an iOS interruption, whose leftovers belong to a moment that has passed.
 */
const PENDING_FRESH_MS = 200;

/**
 * The game's voice.
 *
 * Muting is respected before anything is scheduled, and the audio context is
 * only built once the player has actually interacted with the page — browsers
 * refuse to start it any earlier.
 */
export class Sfx {
  private readonly synth: Synth;
  private enabled = true;
  /** When each currently-ringing move-tier voice ends, on the perf clock. */
  private ringing: number[] = [];
  /** The one cue waiting for the context to finish waking, if any. */
  private pending: { cue: Cue; step: number; at: number } | null = null;

  constructor(synth: Synth = new Synth()) {
    this.synth = synth;
  }

  get muted(): boolean {
    return !this.enabled;
  }

  /** Muting never touches the audio context, so it can be set before any gesture. */
  setMuted(muted: boolean): void {
    this.enabled = !muted;
  }

  /** Hook for the first user gesture, which is when audio is allowed to start. */
  unlock(): void {
    if (this.enabled) this.synth.resume();
  }

  /** The page came back to the foreground. */
  wake(): void {
    if (this.enabled) this.synth.wake();
  }

  /** The page went to the background. */
  suspend(): void {
    this.synth.suspend();
  }

  /**
   * Play a cue. `step` lets repeated cues climb a scale — pass the index of
   * the tower or house in the surge.
   */
  play(cue: Cue, step = 0): void {
    // The hidden check comes before resume() so a timer firing in a
    // backgrounded tab can't wake the context we just parked.
    if (!this.enabled || globalThis.document?.hidden) return;
    // Cheap when already running, and recovers a context the browser
    // suspended while the tab was in the background.
    this.synth.resume();
    if (this.synth.unavailable) return;
    // While the context is still waking, park the cue instead of scheduling
    // it — voices put on a frozen clock would all land on one instant when
    // the clock restarts. One slot, latest wins, and flushPending() drops
    // anything stale, so a wake never releases a cluster. This is also what
    // saves a cold session's very first tick, which otherwise races the
    // async resume() started in its own gesture and loses.
    if (!this.synth.ready) {
      this.pending = { cue, step, at: performance.now() };
      this.synth.afterResume(() => this.flushPending());
      return;
    }

    const ring = MOVE_RING[cue];
    if (ring === undefined) {
      CUES[cue](this.synth, step);
      return;
    }

    const now = performance.now();
    this.ringing = this.ringing.filter((end) => end > now);
    if (this.ringing.length >= MOVE_CAP) {
      // The tier table is the limiter: a fourth simultaneous move voice
      // rides at whisper instead of stacking louder.
      this.synth.scaled(DUCK, () => CUES[cue](this.synth, step));
      return;
    }
    this.ringing.push(now + ring * 1000);
    CUES[cue](this.synth, step);
  }

  /** Retry the parked cue, if the wake left it playable and still fresh. */
  private flushPending(): void {
    const parked = this.pending;
    this.pending = null;
    if (!parked || !this.enabled || globalThis.document?.hidden) return;
    if (!this.synth.ready) return;
    if (performance.now() - parked.at > PENDING_FRESH_MS) return;
    this.play(parked.cue, parked.step);
  }
}

/** The instance the game plays through. */
export const sfx = new Sfx();
